import { logger } from '../common/logger';

let wakeLock: WakeLockSentinel | null = null;
let visibilityHandler: (() => void) | null = null;
let debug = false;

/**
 * Request a screen wake lock to prevent the device from sleeping.
 * Automatically re-acquires the lock when the tab becomes visible again.
 * @param debugMode - Enable debug logging
 */
const applyWakeLock = async (debugMode: boolean = false): Promise<WakeLockSentinel | null> => {
    debug = debugMode;

    // Check if Wake Lock API is supported
    if (!("wakeLock" in navigator)) {
        logger(debug, 'info', '[WakeLock] API not supported in this browser');
        return null;
    }

    try {
        logger(debug, 'info', '[WakeLock] Requesting initial wake lock...');
        wakeLock = await navigator.wakeLock.request("screen");
        logger(debug, 'info', '[WakeLock] Acquired successfully');

        // Listen for when the lock is released (e.g., tab hidden)
        wakeLock.addEventListener('release', () => {
            logger(debug, 'info', '[WakeLock] Released (tab hidden or manual release)');
        });

        // Add visibility change handler to re-acquire lock when tab becomes visible
        visibilityHandler = async () => {
            const state = document.visibilityState;
            const isReleased = wakeLock?.released;

            logger(debug, 'info', `[WakeLock] Visibility changed: ${state} | released: ${isReleased}`);

            if (state === "visible") {
                if (isReleased !== false) {
                    logger(debug, 'info', '[WakeLock] Tab visible, lock released - re-acquiring...');
                    try {
                        wakeLock = await navigator.wakeLock.request("screen");
                        logger(debug, 'info', '[WakeLock] Re-acquired successfully');

                        wakeLock.addEventListener('release', () => {
                            logger(debug, 'info', '[WakeLock] Released (tab hidden or manual release)');
                        });
                    } catch (error) {
                        logger(debug, 'error', `[WakeLock] Failed to re-acquire: ${error}`);
                    }
                } else {
                    logger(debug, 'info', '[WakeLock] Tab visible, lock still active - no action needed');
                }
            } else {
                logger(debug, 'info', '[WakeLock] Tab hidden - browser will release lock');
            }
        };

        document.addEventListener("visibilitychange", visibilityHandler);
        logger(debug, 'info', '[WakeLock] Visibility listener attached');

        return wakeLock;
    } catch (error) {
        logger(debug, 'error', `[WakeLock] Failed to acquire: ${error}`);
        return null;
    }
};

/**
 * Release the wake lock and remove the visibility change listener.
 */
const releaseWakeLock = async (): Promise<void> => {
    logger(debug, 'info', '[WakeLock] releaseWakeLock() called');

    // Remove visibility change listener
    if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
        visibilityHandler = null;
        logger(debug, 'info', '[WakeLock] Visibility listener removed');
    }

    // Release the wake lock
    if (wakeLock && !wakeLock.released) {
        try {
            logger(debug, 'info', '[WakeLock] Releasing...');
            await wakeLock.release();
            logger(debug, 'info', '[WakeLock] Released successfully');
        } catch (error) {
            logger(debug, 'error', `[WakeLock] Failed to release: ${error}`);
        }
    } else {
        logger(debug, 'info', '[WakeLock] No active lock to release');
    }
    wakeLock = null;
};

export { applyWakeLock, releaseWakeLock };
